import { CardContainer } from '@/components/Analysis/Idms/styles';
import { TablePaginationContainer } from '@/components/UserManagement/styles';
import { fetchCellValue, pipesAndSpanHeadersUnit } from '@/utils';
import {
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  Tooltip,
} from '@mui/material';
import { tableCellStyle } from '../EquipmentDetails/styles';
import { rowSx, cellSx, paginationStyles } from './styles';
import { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { PipesAndSpanColumns } from '@/types';
import { useUnitConversion } from '@/hooks/useUnitConversion';
import * as state from '@/components/Analysis/state';
import { useAtomValue } from 'jotai';
import { useGetStructureId } from '@/hooks/useGetStructureId';

type Props = {
  loading?: boolean;
  tableRows: Record<string, unknown>[];
  columns: PipesAndSpanColumns[];
};

export const GenericIdmsTable = ({ tableRows, loading, columns }: Props) => {
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const { getUnitSuffix } = useUnitConversion();
  const structureId = useGetStructureId();
  const structurePipesByStructureId = useAtomValue(state.structurePipesByStructureId);
  const unitSystem = useAtomValue(state.unitSystem);
  const paginatedRows = useMemo(
    () => tableRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [tableRows, page, rowsPerPage]
  );
  const handleChangeRowsPerPage = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setRowsPerPage(Number(event.target.value));
      setPage(0);
    },
    []
  );

  const handleChangePage = useCallback((_event: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  if (tableRows.length === 0) return <></>;
  return (
    <Paper elevation={0} sx={{ padding: 1, borderRadius: 2 }}>
      <TableContainer sx={{ overflowX: 'auto' }}>
        {loading ? (
          <CardContainer>
            <Skeleton variant="rectangular" width="100%" height={300} />
          </CardContainer>
        ) : paginatedRows.length > 0 ? (
          <Table
            sx={{
              borderCollapse: 'collapse',
              tableLayout: 'auto',
            }}
          >
            <TableHead>
              <TableRow>
                {columns.map((column) => {
                  return (
                    <TableCell
                      key={column.key}
                      align="center"
                      sx={{
                        ...tableCellStyle,
                        border: 0,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <Typography
                        sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase' }}
                      >
                        {column.displayUnit
                          ? `${column.displayName} (${pipesAndSpanHeadersUnit(
                              getUnitSuffix,
                              column.displayUnit
                            )})`
                          : column.displayName}
                      </Typography>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>

            <TableBody>
              {paginatedRows.map((row, rIndex) => (
                <TableRow key={rIndex} sx={rowSx}>
                  {columns.map((col) => {
                    const cellResult = fetchCellValue(
                      row,
                      col.key,
                      unitSystem,
                      structurePipesByStructureId[structureId],
                      col.displayUnit,
                      Number(col.roundToDecimal)
                    );
                    const { rawValue, roundedValue } = cellResult;
                    return (
                      <TableCell
                        key={col.key}
                        sx={{
                          ...tableCellStyle,
                          ...cellSx,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <Typography sx={{ fontSize: 10, fontWeight: 400 }} noWrap>
                          {col.roundToDecimal ? (
                            <Tooltip title={String(rawValue)}>
                              <span>{String(roundedValue ?? 'N/A')}</span>
                            </Tooltip>
                          ) : (
                            String(rawValue ?? 'N/A')
                          )}
                        </Typography>
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Typography sx={{ fontSize: 10, fontWeight: 400 }}>No data available</Typography>
        )}

        {tableRows.length > 0 && (
          <TablePaginationContainer>
            <TablePagination
              slotProps={{ select: { sx: { m: 0, p: 0, fontSize: '1rem' } } }}
              sx={paginationStyles}
              count={tableRows?.length ?? 0}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPageOptions={[
                { label: '10', value: 10 },
                { label: '25', value: 25 },
                { label: '100', value: 100 },
                { label: 'All', value: tableRows?.length ?? 0 },
              ]}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </TablePaginationContainer>
        )}
      </TableContainer>
    </Paper>
  );
};
